<template>
	<div :class="classes" :style="styles" @mousedown.stop="setTravelOrigin($event)">
		<div v-if="selected" :class="resizeClasses" :style="topLeftCornerStyles" @mousedown.stop="startResizingTopLeft($event)"/>
		<div v-if="selected" :class="resizeClasses" :style="topRightCornerStyles" @mousedown.stop="startResizingTopRight($event)"/>
		<div v-if="selected" :class="resizeClasses" :style="bottomLeftCornerStyles" @mousedown.stop="startResizingBottomLeft($event)"/>
		<div v-if="selected" :class="resizeClasses" :style="bottomRightCornerStyles" @mousedown.stop="startResizingBottomRight($event)"/>
		<div v-if="selected" :class="resizeClasses" :style="leftStyles" @mousedown.stop="startResizingLeft($event)"/>
		<div v-if="selected" :class="resizeClasses" :style="rightStyles" @mousedown.stop="startResizingRight($event)"/>
		<div v-if="selected" :class="resizeClasses" :style="topStyles" @mousedown.stop="startResizingTop($event)"/>
		<div v-if="selected" :class="resizeClasses" :style="bottomStyles" @mousedown.stop="startResizingBottom($event)"/>
		<template v-if="module.cell">
			<textarea v-if="editingText && selected" :id="textEditorId" v-model="module.cell.txt" type="text" class="z-10 border-0 p-0 inline-block w-full h-full overflow-hidden !bg-transparent resize-none" :style="textStyles"/>
			<div v-else class="inline-block w-full h-full overflow-hidden" :style="textStyles" @dblclick="emit('update:editingText', true)" v-html="module.cell.txt"/>
		</template>
		<div v-else-if="module.qr" class="inline-block w-full bg-center bg-no-repeat bg-cover" :style="qrStyles">
			<a v-if="!module.qr.code" href="#" :style="textStyles">https://aareii.org.ar/certificates/{id}/c5e3da018a3e404ffe85c97a12a9912d/verify</a>
		</div>
		<div v-else class="inline-block w-full bg-no-repeat bg-cover" :style="textStyles"/>
	</div>
</template>
<script setup lang="ts">
import {storage} from '@/Helpers/general'
import {type CertificateModule} from '@/Types/Certificates/CertificateModule'
import {computed, type ComputedRef, defineEmits, defineProps, onMounted, type Ref, ref} from 'vue'

interface OriginValues {
	pointer: {
		x: number,
		y: number,
	},
	x: number,
	y: number,
	w: number,
	h: number,
}

const props = defineProps<{
	maxWidth:number,
	maxHeight:number,
	selected:boolean,
	editingText:boolean,
	modelValue:CertificateModule,
}>()

const emit = defineEmits([
	'select',
	'delete',
	'update:modelValue',
	'update:editingText',
])

const maxWidth:Ref<number> = ref(props.maxWidth)
const maxHeight:Ref<number> = ref(props.maxHeight)
const module:Ref<CertificateModule> = ref(props.modelValue)
const originValues:Ref<OriginValues> = ref({
	pointer: {
		x: 0,
		y: 0
	},
	x: props.modelValue.x,
	y: props.modelValue.y,
	w: props.modelValue.w,
	h: props.modelValue.h,
})
const resizingTop:Ref<boolean> = ref(false)
const resizingLeft:Ref<boolean> = ref(false)
const resizingRight:Ref<boolean> = ref(false)
const resizingBottom:Ref<boolean> = ref(false)
const movingVertically:Ref<boolean> = ref(false)
const mouseEvent:Ref<MouseEvent|null> = ref(null)
const movingHorizontally:Ref<boolean> = ref(false)
const textEditorId:Ref<string> = ref(`certificate-text-editor-${props.modelValue.id}`)

const moving:ComputedRef<boolean> = computed(() => {
	return movingVertically.value || movingHorizontally.value
})
const resizing:ComputedRef<boolean> = computed(() => {
	return resizingTop.value || resizingLeft.value || resizingRight.value || resizingBottom.value
})
const resizeClasses:ComputedRef<string> = computed(() => {
	return 'border border-gray-200 inline-flex absolute rounded bg-blue-500 z-40'
})
const boxMarkerHalfX:ComputedRef<number> = computed(() => {
	return (width.value) / 2 - 4.5
})
const boxMarkerHalfY:ComputedRef<number> = computed(() => {
	return (height.value) / 2 - 4.5
})
const topLeftCornerStyles:ComputedRef<string> = computed(() => {
	return 'width: 7px; height: 7px; margin-left: -4px; margin-top: -4px; cursor: nwse-resize;'
})
const topRightCornerStyles:ComputedRef<string> = computed(() => {
	return `width: 7px; height: 7px; margin-left: ${width.value - 5}px; margin-top: -4px; cursor: nesw-resize;`
})
const bottomLeftCornerStyles:ComputedRef<string> = computed(() => {
	return `width: 7px; height: 7px; margin-left: -4px; margin-top: ${height.value - 5}px; cursor: nesw-resize;`
})
const bottomRightCornerStyles:ComputedRef<string> = computed(() => {
	return `width: 7px; height: 7px; margin-left: ${width.value - 5}px; margin-top: ${height.value - 5}px; cursor: nwse-resize;`
})
const leftStyles:ComputedRef<string> = computed(() => {
	return `width: 7px; height: 7px; margin-left: -4px; margin-top: ${boxMarkerHalfY.value}px; cursor: ew-resize;`
})
const rightStyles:ComputedRef<string> = computed(() => {
	return `width: 7px; height: 7px; margin-left: ${width.value - 5}px; margin-top: ${boxMarkerHalfY.value}px; cursor: ew-resize;`
})
const topStyles:ComputedRef<string> = computed(() => {
	return `width: 7px; height: 7px; margin-left: ${boxMarkerHalfX.value}px; margin-top: -4px; cursor: ns-resize;`
})
const bottomStyles:ComputedRef<string> = computed(() => {
	return `width: 7px; height: 7px; margin-left: ${boxMarkerHalfX.value}px; margin-top: ${height.value - 5}px; cursor: ns-resize;`
})
const classes:ComputedRef<string> = computed(() => {
	return 'border border-gray-200 select-none inline-flex absolute z-30'
})
const styles:ComputedRef<string> = computed(() => {
	return `margin-left: ${positionLeft.value - 1}px; margin-top: ${positionTop.value - 1}px; cursor: move; width: ${width.value}px; height: ${height.value}px;`
})
const qrStyles:ComputedRef<string> = computed(() => {
	let style = ''
	if(module.value.qr && module.value.qr.code){
		style += `background-image: url('${storage('certificates/qr/placeholder.png')}');`
	}
	return style
})
const textStyles:ComputedRef<string> = computed(() => {
	let style = ''
	if(module.value.qr){
		style += `font-family: '${module.value.qr.font}', 'Arial', sans-serif !important; font-size: ${module.value.qr.font_size}pt;`
		if(module.value.qr.align === 'L'){
			style += "text-align: left;"
		} else if(module.value.qr.align === 'C'){
			style += "text-align: center;"
		} else if(module.value.qr.align === 'R'){
			style += "text-align: right;"
		}
		if(module.value.qr.bold){
			style += "font-weight: bold;"
		}
		if(module.value.qr.italic){
			style += "font-style: italic;"
		}
		if(module.value.qr.underlined){
			style += "text-decoration: underline;"
		}
		if(module.value.qr.color){
			style += `color: ${module.value.qr.color};`
		}
	} else if(module.value.cell){
		style += `font-family: '${module.value.cell.font}', 'Arial', sans-serif; font-size: ${module.value.cell.font_size}pt;`
		if(module.value.cell.align === 'L'){
			style += "text-align: left;"
		} else if(module.value.cell.align === 'C'){
			style += "text-align: center;"
		} else if(module.value.cell.align === 'R'){
			style += "text-align: right;"
		}
		if(module.value.cell.bold){
			style += "font-weight: bold;"
		}
		if(module.value.cell.italic){
			style += "font-style: italic;"
		}
		if(module.value.cell.underlined){
			style += "text-decoration: underline;"
		}
		if(module.value.cell.color){
			style += `color: ${module.value.cell.color};`
		}
	} else if(module.value.image && module.value.image.file){
		style += `background-image: url('${storage(module.value.image.file)}');`
	}
	return style
})
const xDrag:ComputedRef<number> = computed(() => {
	let drag = 0
	if(mouseEvent.value){
		drag = mouseEvent.value.clientX - originValues.value.pointer.x
	}
	return drag
})
const yDrag:ComputedRef<number> = computed(() => {
	let drag = 0
	if(mouseEvent.value){
		drag = mouseEvent.value.clientY - originValues.value.pointer.y
	}
	return drag
})
const shortestDrag:ComputedRef<number> = computed(() => {
	return Math.abs(xDrag.value) < Math.abs(yDrag.value) ? xDrag.value : yDrag.value
})
const ratio:ComputedRef<number> = computed(() => {
	return originValues.value.w / originValues.value.h
})
const positionLeft:ComputedRef<number> = computed(() => {
	let position = module.value.x
	let offset = xDrag.value
	if(module.value.lock_ratio && resizing.value){
		if(!resizingLeft.value){
			offset = yDrag.value / 2
		}
		if(resizingTop.value && !resizingRight.value){
			if(resizingLeft.value){
				offset = shortestDrag.value
			}
			position = originValues.value.x + (offset * ratio.value)
		} else if(resizingBottom.value && !resizingRight.value){
			position = originValues.value.x + (offset * -1 * ratio.value)
		}
	}
	if(movingHorizontally.value && !resizingRight.value){
		position = originValues.value.x + offset
	}
	return Math.max(0, Math.min(maxWidth.value - width.value, position))
})
const positionTop:ComputedRef<number> = computed(() => {
	let position = module.value.y
	let offset = yDrag.value
	if(module.value.lock_ratio && resizing.value){
		if(!resizingTop.value){
			offset = xDrag.value / 2
		}
		if(resizingLeft.value && !resizingBottom.value){
			if(resizingTop.value){
				offset = shortestDrag.value
			}
			position = originValues.value.y + (offset * ratio.value)
		} else if(resizingRight.value && !resizingBottom.value){
			position = originValues.value.y + (offset * -1 * ratio.value)
		}
	}
	if(movingVertically.value && !resizingBottom.value){
		position = originValues.value.y + offset
	}
	return Math.max(0, Math.min(maxHeight.value - height.value, position))
})
const width:ComputedRef<number> = computed(() => {
	let width = module.value.w
	if(module.value.lock_ratio){
		if(resizingTop.value){
			if(resizingLeft.value){
				// TOP LEFT
				width = originValues.value.w + (shortestDrag.value * -1 * ratio.value)
			} else if(resizingRight.value){
				// TOP RIGHT
				let offset = shortestDrag.value
				if(
					(xDrag.value < 0 && yDrag.value > 0 && Math.abs(xDrag.value) > yDrag.value)
					||
					(xDrag.value > 0 && yDrag.value < 0 && Math.abs(yDrag.value) < xDrag.value)
				){
					offset *= -1
				}
				width = originValues.value.w + (offset * ratio.value) // TODO
			} else {
				// TOP
				width = originValues.value.w + (yDrag.value * -1 * ratio.value)
			}
		} else if(resizingBottom.value){
			if(resizingLeft.value){
				// BOTTOM LEFT
				width = originValues.value.w + (shortestDrag.value * -1 * ratio.value) // TODO
			} else if(resizingRight.value){
				// BOTTOM RIGHT
				width = originValues.value.w + (shortestDrag.value * ratio.value)
			} else {
				// BOTTOM
				width = originValues.value.w + (yDrag.value * ratio.value)
			}
		} else if(resizingLeft.value){
			// LEFT
			width = originValues.value.w - xDrag.value
		} else if(resizingRight.value){
			// RIGHT
			width = originValues.value.w + xDrag.value
		}
	} else if(resizingLeft.value){
		width = originValues.value.w - xDrag.value
	} else if(resizingRight.value){
		width = originValues.value.w + xDrag.value
	}
	return Math.min(maxWidth.value, Math.max(30, width))
})
const height:ComputedRef<number> = computed(() => {
	let height = module.value.h
	if(module.value.lock_ratio){
		if(resizingLeft.value){
			if(resizingTop.value){
				// TOP LEFT
				height = originValues.value.h + (shortestDrag.value * -1 * ratio.value)
			} else if(resizingBottom.value){
				// BOTTOM LEFT
				height = originValues.value.h + (shortestDrag.value * ratio.value) // TODO
			} else {
				// LEFT
				height = originValues.value.h + (xDrag.value * -1 * ratio.value)
			}
		} else if(resizingRight.value){
			if(resizingTop.value){
				// TOP RIGHT
				let offset = shortestDrag.value
				if(
					(xDrag.value > 0 || yDrag.value < 0 || Math.abs(xDrag.value) > yDrag.value)
					&&
					(xDrag.value < 0 || yDrag.value > 0 || Math.abs(yDrag.value) < xDrag.value)
				){
					offset *= -1
				}
				height = originValues.value.h + (offset * ratio.value) // TODO
			} else if(resizingBottom.value){
				// BOTTOM RIGHT
				height = originValues.value.h + (shortestDrag.value * ratio.value)
			} else {
				// RIGHT
				height = originValues.value.h + (xDrag.value * ratio.value)
			}
		} else if(resizingTop.value){
			// TOP
			height = originValues.value.h - yDrag.value
		} else if(resizingBottom.value){
			// BOTTOM
			height = originValues.value.h + yDrag.value
		}
	} else if(resizingTop.value){
		height = originValues.value.h - yDrag.value
	} else if(resizingBottom.value){
		height = originValues.value.h + yDrag.value
	}
	return Math.min(maxHeight.value, Math.max(30, height))
})

function setTravelOrigin(e:MouseEvent):void{
	if(e.target && (e.target as HTMLElement).id !== textEditorId.value){
		mouseEvent.value = e
		setOriginValues()
		movingVertically.value = true
		movingHorizontally.value = true
		emit('select')
	}
}

function setOriginValues():void{
	if(mouseEvent.value){
		originValues.value.pointer.x = mouseEvent.value.clientX
		originValues.value.pointer.y = mouseEvent.value.clientY
	}
	originValues.value.x = module.value.x
	originValues.value.y = module.value.y
	originValues.value.w = module.value.w
	originValues.value.h = module.value.h
}

function startResizingTopLeft(e:MouseEvent):void{
	startResizingTop(e)
	startResizingLeft(e)
}

function startResizingTopRight(e:MouseEvent):void{
	startResizingTop(e)
	startResizingRight(e)
}

function startResizingBottomLeft(e:MouseEvent):void{
	startResizingBottom(e)
	startResizingLeft(e)
}

function startResizingBottomRight(e:MouseEvent):void{
	startResizingBottom(e)
	startResizingRight(e)
}

function startResizingTop(e:MouseEvent):void{
	mouseEvent.value = e
	setOriginValues()
	resizingTop.value = true
	movingVertically.value = true
}

function startResizingLeft(e:MouseEvent):void{
	mouseEvent.value = e
	setOriginValues()
	resizingLeft.value = true
	movingHorizontally.value = true
}

function startResizingRight(e:MouseEvent):void{
	mouseEvent.value = e
	setOriginValues()
	resizingRight.value = true
}

function startResizingBottom(e:MouseEvent):void{
	mouseEvent.value = e
	setOriginValues()
	resizingBottom.value = true
}

function update():void{
	module.value.w = width.value
	module.value.h = height.value
	module.value.y = positionTop.value
	module.value.x = positionLeft.value
}

function stop():void{
	mouseEvent.value = null
	movingVertically.value = false
	movingHorizontally.value = false
	resizingLeft.value = false
	resizingRight.value = false
	resizingTop.value = false
	resizingBottom.value = false
}

onMounted(() => {
	window.addEventListener('mousemove', (e) => {
		if(resizing.value || moving.value){
			mouseEvent.value = e
			update()
			emit('update:modelValue', module.value)
		}
	})
	window.addEventListener('mouseup', () => {
		if(resizing.value || moving.value){
			stop()
		}
	})
})
</script>
